/**
 * projectLayers — merge every typed layer of a project's live state
 * onto its base graph in one place.
 *
 * Layers stacked (each contributes nodes and/or edges):
 *   1. base graph from store (sample / Foyr-bundle ingested)
 *   2. user placements (IN_ROOM edges from lib/userPlacements)
 *   3. imported SKUs (from lib/catalogIngest.readImportedCatalog)
 *   4. CatalogImport prov:Activity nodes + WAS_GENERATED_BY edges
 *      (from lib/catalogIngest.readImportActivities)
 *   5. schedule synth (from lib/agent/scheduleSynth.readSynthSchedule)
 *      — adds typed Milestones + DEPENDS_ON + provenance edges
 *
 * Design.tsx and Graph.tsx (project map) both call this so the typed
 * provenance edges that catalogIngest emits are visible everywhere.
 * Dedupes by id so calling twice on the same baseGraph is a no-op.
 */
import type { ProjectGraph } from "../types/ontology";
import { mergeUserPlacements } from "./userPlacements";
import { mergeUserFinishes } from "./userFinishes";
import { readImportActivities, readImportedCatalog } from "./catalogIngest";
import { readSynthSchedule } from "./agent/scheduleSynth";
import { asGraphNodes as userTasksAsGraphNodes } from "./userTasks";

export function mergeProjectLayers(baseGraph: ProjectGraph, projectId: string): ProjectGraph {
  // Order matters — userPlacements drops any placement whose skuId isn't
  // already a node, so imported catalog SKUs MUST land first. The earlier
  // ordering silently dropped Living-room placements for IKEA PAX etc.,
  // because the imported SKU node wasn't visible to mergeUserPlacements yet.

  // 1 — imported catalog SKUs as first-class graph nodes.
  const imported = readImportedCatalog(projectId);
  // 2 — typed CatalogImport activities + provenance edges.
  const activities = readImportActivities(projectId);
  // 3 — user tasks (typed Alert with role="task").
  const userTasks = userTasksAsGraphNodes(projectId);

  const baseNodeIds = new Set(baseGraph.nodes.map((n) => n.id));
  const baseEdgeIds = new Set(baseGraph.edges.map((e) => e.id));
  const layeredNodes = [
    ...baseGraph.nodes,
    ...imported.filter((n) => !baseNodeIds.has(n.id)),
    ...activities.nodes.filter((n) => !baseNodeIds.has(n.id)),
    ...userTasks.filter((n) => !baseNodeIds.has(n.id)),
  ];
  const layeredEdges = [
    ...baseGraph.edges,
    ...activities.edges.filter((e) => !baseEdgeIds.has(e.id)),
  ];
  const withImports: ProjectGraph = { ...baseGraph, nodes: layeredNodes, edges: layeredEdges };

  // 4 — NOW merge userPlacements; the IN_ROOM edges + posX/posZ overrides
  //     can resolve their target SKU because imported nodes are visible.
  let g = mergeUserPlacements(withImports, projectId);

  // 4b — Designer-applied finishes (per sku, room → Material) write
  //      finishMaterialId_${roomId} + finishLabel_${roomId} into the
  //      SKU attrs so BOM, agent suggestions and the Why-engine read
  //      them via the standard typed-attr path.
  g = mergeUserFinishes(g, projectId);

  // 5 — agent-synthesized schedule (typed Milestones + DEPENDS_ON +
  //     WAS_GENERATED_BY back to the Synthesis activity).
  const synth = readSynthSchedule(projectId);
  if (synth) {
    const existingNodeIds = new Set(g.nodes.map((n) => n.id));
    const existingEdgeIds = new Set(g.edges.map((e) => e.id));
    const freshNodes = [];
    if (!existingNodeIds.has(synth.activity.id)) freshNodes.push(synth.activity);
    for (const m of synth.milestones) {
      if (!existingNodeIds.has(m.id)) freshNodes.push(m);
    }
    const freshEdges = [];
    for (const e of [...synth.dependsOn, ...synth.wasGeneratedBy]) {
      if (!existingEdgeIds.has(e.id)) freshEdges.push(e);
    }
    if (freshNodes.length > 0 || freshEdges.length > 0) {
      g = { ...g, nodes: [...g.nodes, ...freshNodes], edges: [...g.edges, ...freshEdges] };
    }
  }
  return g;
}
